import React from 'react';
import { ShapeBlock } from '../../ast/document.types';

interface ShapeBlockRendererProps {
  block: ShapeBlock;
}

export const ShapeBlockRenderer: React.FC<ShapeBlockRendererProps> = ({ block }) => {
  const {
    shapeType = 'rectangle',
    fill = '#f0f4f8',
    stroke = '#1e3a8a',
    strokeWidth = 1.5,
    strokeStyle = 'solid',
    opacity = 1,
    width = 160,
    height = 80,
    rotation = 0,
    alignment = 'center',
    offsetX = 0,
    offsetY = 0,
  } = block.data;

  const getDashArray = () => {
    if (strokeStyle === 'dashed') return '6,4';
    if (strokeStyle === 'dotted') return '2,3';
    return undefined;
  };

  const dashArray = getDashArray();
  const safeWidth = Math.max(10, width);
  const safeHeight = Math.max(2, height);
  const sw = Math.max(0.5, strokeWidth);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center',
    margin: '8px 0',
    transform: `translate(${offsetX}px, ${offsetY}px)`,
    transition: 'transform 0.05s ease-out',
    width: '100%',
  };

  const svgStyle: React.CSSProperties = {
    opacity,
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    transformOrigin: 'center center',
    overflow: 'visible',
  };

  const arrowMarkerId = `arrow-${block.id}`;

  const renderShapeContent = () => {
    switch (shapeType) {
      case 'rectangle':
        return (
          <rect
            x={sw / 2}
            y={sw / 2}
            width={safeWidth - sw}
            height={safeHeight - sw}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeDasharray={dashArray}
          />
        );

      case 'rounded_rectangle':
        return (
          <rect
            x={sw / 2}
            y={sw / 2}
            width={safeWidth - sw}
            height={safeHeight - sw}
            rx={10}
            ry={10}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeDasharray={dashArray}
          />
        );

      case 'circle': {
        const radius = Math.max(2, Math.min(safeWidth, safeHeight) / 2 - sw);
        return (
          <circle
            cx={safeWidth / 2}
            cy={safeHeight / 2}
            r={radius}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeDasharray={dashArray}
          />
        );
      }

      case 'ellipse': {
        const rx = Math.max(2, safeWidth / 2 - sw);
        const ry = Math.max(2, safeHeight / 2 - sw);
        return (
          <ellipse
            cx={safeWidth / 2}
            cy={safeHeight / 2}
            rx={rx}
            ry={ry}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeDasharray={dashArray}
          />
        );
      }

      case 'line':
        return (
          <line
            x1={0}
            y1={safeHeight / 2}
            x2={safeWidth}
            y2={safeHeight / 2}
            stroke={stroke}
            strokeWidth={sw}
            strokeDasharray={dashArray}
            strokeLinecap="round"
          />
        );

      case 'arrow':
        return (
          <>
            <defs>
              <marker
                id={arrowMarkerId}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={stroke} />
              </marker>
            </defs>
            <line
              x1={4}
              y1={safeHeight / 2}
              x2={safeWidth - 10}
              y2={safeHeight / 2}
              stroke={stroke}
              strokeWidth={sw}
              strokeDasharray={dashArray}
              strokeLinecap="round"
              markerEnd={`url(#${arrowMarkerId})`}
            />
          </>
        );

      case 'triangle': {
        const p1 = `${safeWidth / 2},${sw}`;
        const p2 = `${safeWidth - sw},${safeHeight - sw}`;
        const p3 = `${sw},${safeHeight - sw}`;
        return (
          <polygon
            points={`${p1} ${p2} ${p3}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeDasharray={dashArray}
            strokeLinejoin="round"
          />
        );
      }

      case 'divider':
        return (
          <g>
            <line
              x1={0}
              y1={safeHeight / 2}
              x2={safeWidth}
              y2={safeHeight / 2}
              stroke={stroke}
              strokeWidth={sw}
              strokeDasharray={dashArray}
            />
            {/* Academic center notch / diamond */}
            <polygon
              points={`${safeWidth / 2},${safeHeight / 2 - 4} ${safeWidth / 2 + 4},${safeHeight / 2} ${safeWidth / 2},${safeHeight / 2 + 4} ${safeWidth / 2 - 4},${safeHeight / 2}`}
              fill={stroke}
            />
          </g>
        );

      default:
        return (
          <rect
            x={0}
            y={0}
            width={safeWidth}
            height={safeHeight}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
          />
        );
    }
  };

  return (
    <div className="doc-shape-block-container" style={containerStyle}>
      <svg
        width={safeWidth}
        height={safeHeight}
        viewBox={`0 0 ${safeWidth} ${safeHeight}`}
        style={svgStyle}
      >
        {renderShapeContent()}
      </svg>
    </div>
  );
};
